import React from 'react';
import {ExtensionsListItem} from '../../../store/reducers/extensions/extensions/reducer';
import {Column} from 'react-table';
import {Assignment, CloudDownload, Delete, StarRate} from '@material-ui/icons';
import {convertFormatFromBackendToDayJs, convertToUserFormat} from '../../../utils/dateWithTimezoneConversion';
import {Colors} from '../../../styles/Colors';
import * as Yup from 'yup';
import {TFunction} from 'react-i18next';
import {makeStyles} from '@material-ui/core';
import dayjs from '../../../services/customDayJs';
import CustomizedIconButton from '../../../components/IconButton/IconButton';
import i18n from '../../../services/i18n';
import DateTimeCell from '../../../components/Calls/DateTimeCell';
import {Voicemail} from '../../../store/actions/payloads';
import classNames from 'classnames';
import PlayFileButton from '../../../components/PlayFileButton/PlayFileButton';
import {ReactComponent as UnifiedMessagingIcon} from '../../../assets/services/unified_messaging.svg';
import {VoicemailDetailsDialogProps} from './VoicemailDetailsDialog.utils';
import TwoLineCell from "../../../components/Calls/TwoLineCell";
import {ReactComponent as FaxIcon} from '../../../assets/faxmailbox_blue.svg';
import {voicemailPlaybackKey} from '../../../utils/playbackIdentity';


export type VoicemailsDialogProps = {
    isOpen: boolean;
    toggleVisibility: () => void;
    extensionId: string;
    extensionName: string;
    i_account: number;
    fetchData: () => void;
    faxMode: boolean,
};

export type VoicemailsFormProps = {
    from: string;
    till: string;
    cli?: string;
    cld?: string;
};

export const dateFormat = 'YYYY-MM-DD HH:mm:ss';

const emptyDateError = i18n.t('errors:ringGroups.emptyInput');
const wrongFormat = i18n.t('errors:ringGroups.wrongFormat');

const dateTestFrom = Yup.string()
    .required(emptyDateError)
    .test('is-valid-date', wrongFormat, (v) =>
        dayjs(new Date(v?.replace(/-/g, '/') || '')).isValid(),
    );

const dateTestTill = Yup.string()
    .required(emptyDateError)
    .test('is-valid-date', wrongFormat, (v) =>
        dayjs(new Date(v?.replace(/-/g, '/') || '')).isValid(),
    );

export const dateFromDateTillValidationSchema = (timezoneOffset: number, userDateTimeFormat: string) => {
    return Yup.object().shape({
        from: dateTestFrom.test(
            'dobY',
            '',
            (value, ctx) => {
                const from = value
                    ? dayjs(value, dateFormat).toDate()
                    : null;
                const till = Yup.ref('till')
                    ? dayjs(
                        ctx.parent.till,
                        dateFormat,
                    ).toDate()
                    : null;

                // @ts-ignore
                return !ctx.parent.till || from < till ? true
                    : ctx.createError({
                        message: i18n.t<string>('errors:common.enterDateBefore', {
                            value: convertToUserFormat(
                                ctx.parent.till,
                                convertFormatFromBackendToDayJs(userDateTimeFormat || dateFormat)),
                        }),
                        path: 'from',
                    });
            },
        ),
        till: dateTestTill
    })
};

export const dateFromDateTillValidationSchema2 = () => {
    return Yup.object().shape({
        beforeFilter: Yup.date().typeError(wrongFormat),
        afterFilter: Yup.date().typeError(wrongFormat),
        coveringFilter: Yup.date().typeError(wrongFormat)
    })
};

export interface CallHistoryDialogProps {
    isOpen: boolean;
    toggleVisibility?: () => void;
    accountId?: number;
    config?: ExtensionsListItem | null;
    customHeader?: string;
    downloadRecordingPermission?: string[];

    hiddeName?: boolean
}

// @ts-ignore
export const useStyles = makeStyles((theme) => ({
    itemsContainer: {
        display: 'flex',
        alignItems: 'flex-start',

        '& .MuiFormControl-root': {
            marginRight: 15,
            width: 209,
        },
    },
    inputs: {
        '& .MuiFormControl-root:first-of-type': {
            marginRight: 90,
        },

        '& .MuiInputBase-root': {
            height: 60,
            marginTop: 0,
        },
    },
    primaryActionButton: {
        '& .MuiButton-label': {
            color: Colors.Primary,
        },
    },
    header: {
        '& .MuiTypography-h6': {
            fontWeight: 'bold',
            fontSize: 18,
        },
    },
    modalContainer: {
        '& .MuiDialogContent-root': {
            background: Colors.SmokeBackground,
            width: 1002,
            padding: '36px 32px',
            overflowX: 'hidden'
        },
    },
    button: {
        '& .MuiButton-label': {
            color: Colors.White,
        },
        marginTop: 10,
    },
    mainContainer: {
        width:1040
    },
    table: (props: {forAccount:boolean}) => ({
        marginTop: 30,

        '& .MuiTableBody-root': {
            '& .MuiTableRow-root': {
                height: 66,
                display: 'flex',
                alignItems: 'center',
            },
        },

        '& thead th:nth-child(3)': {
            textAlign: 'left !important'
        },

        '& thead th:nth-child(4)': {
            textAlign: props.forAccount ? 'left !important' : 'center !important'
        },

        '& thead th:nth-child(5)': {
            textAlign: 'center !important'
        },

        '& thead th:nth-child(6)': {
            textAlign: 'center !important'
        },

        '& thead th:nth-child(7)': {
            textAlign: 'center !important'
        },
        '& thead th:nth-child(8)': {
            textAlign:'center !important'
        },
    }),
    pagination: {
        color: Colors.Gray,
    },
    centeredRecordingValue: {
        paddingLeft: '0!important',
        marginLeft: 10,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    headerCell: {
        fontSize: 16,
    },
    filterSeparation: {
        paddingBottom: 25,
    },
    connectTime: {
        color: Colors.Gray5,
        fontSize: 14,
    },
    timePicker: {
        '& .MuiFormHelperText-root': {
            marginLeft: 0,
            marginRight: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            fontSize: '0.73rem'
        },
    },
    badge: {
        fontWeight: 400,
        fontSize: 12,
        lineHeight: '20px',
        minWidth: 48,
        width: 'fit-content',
        height: 20,
        borderRadius: 16,
        textAlign: 'center',
        padding: '0px 8px',
        border: '2px solid white'
    },
    activeBadge: {
        color: Colors.White,
        background: 'rgba(245, 132, 31, 1)'
    },
    inactiveBadge: {
        color: 'rgba(62, 92, 105, 1)',
        background: 'rgba(238, 240, 243, 1)'
    },
    playPauseButtonLoading: {
        color: Colors.Primary2,
        height: '28px !important',
        width: '28px !important',

        '& .MuiCircularProgress-svg': {
            color: Colors.Primary2
        },

        marginTop: 6,
        marginLeft: 8,
        marginRight: 8,
    },
    answered: {
        color: Colors.White,
        background: 'rgba(117, 185, 67, 1)'
    },
    answeredOverSeen: {
        position: 'relative',
        left: -10,
        zIndex: 2,
    },
    sizeTextContainer: {
        textAlign: 'right',
        paddingRight: 73,
        width: '100%'
    },
    timeTextContainer: {
        width: '100%',
        textAlign: 'left',
        whiteSpace: 'pre-line'
    },

    caller:{
        textAlign: 'left'
    },

    destination:{
        textAlign: 'left'

    },

    durationTextContainer: {
        width: '100%',
        textAlign: 'center'
    },
    statusContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'row'
    },
    confirmDialogContainer: {
        '& .MuiDialogContent-root': {
            minWidth: 'unset',
        },
        '& .MuiPaper-root': {
            width: 336,
        },
    },
    playingMusicRow: {
        background: '#FFF5DD !important',
        borderLeft: '4px solid ' + Colors.Primary2,

        '& [custom-attrib-file-counter="custom-attrib-file-counter"]': {
            background: Colors.Primary2,
            color: Colors.White,

            '& svg > path': {
                fill: Colors.White
            }
        },
    },
    starred: {
        color: 'rgba(245, 132, 31, 1)',

        '& svg > path': {
            fill: 'rgba(245, 132, 31, 1)'
        },

        cursor: 'pointer'
    },
    notStarred: {
        color: 'rgba(197, 213, 230, 1)',

        '& svg > path': {
            fill: 'rgba(197, 213, 230, 1)'
        },

        cursor: 'pointer'
    },
    emptyActionDiv: {
        width: 44,
        height: '100%'
    }
}));


export const generateAccountColumns = (
    t: TFunction<string>,
    classes: ReturnType<typeof useStyles>,
    timezoneOffset: number,
    userDateTimeFormat: string,
    downloadByMessageUid: (messageid: number, callback: (url: string) => void, restrictedCallback?: () => void) => void,
    onClickDelete: (messageid: number) => void,
    onClickStarred: (messageid: number, newValue: boolean) => void,
    onClickDetails: (voicemail: Voicemail) => void,
): Column<Voicemail>[] => {
    return [
        {
            accessor: 'not_used',
            Header: '',
            width: 0.1,
            Cell: function Cell(params) {
                const flagged = params.row.original.flags?.includes('\\Flagged');
                return (<div className={classes.statusContainer}>
                    <CustomizedIconButton
                        onClick={() => {
                            onClickStarred(params.row.original.message_uid, !flagged);
                        }}
                        dataTestId={'flagged-icon'}
                    >
                        <StarRate
                            className={flagged ? classes.starred : classes.notStarred}
                        />
                    </CustomizedIconButton>
                </div>);
            },
        },
        {
            accessor: 'delivery_date',
            Header: t<string>('screens:voicemails.deliveryTime'),
            width: 0.6,
            Cell: function Cell(params) {

                return (
                        <DateTimeCell
                            timezoneOffset={timezoneOffset}
                            time={params.row.original.delivery_date}
                            customDateFormat={userDateTimeFormat}
                            showEmptyOnNoDate
                        />
                        );
            },
        },
        {
            accessor: 'caller',
            Header: t<string>('screens:calls.caller'),
            width: 0.8,
            Cell: function Cell(params) {
                if(params.row.original?.caller_splits?.length === 2)
                {
                    return <TwoLineCell headerText={params.row.original?.caller_splits[0]} descriptionText={params.row.original?.caller_splits[1]} />
                }

                return (
                    <div className={classes.caller}>{params.row.original.caller ?? ''}</div>
                );
            },
        },
        {
            accessor: 'destination',
            Header: t<string>('screens:calls.calledNumber'),
            width: 0.6,
            Cell: function Cell(params) {

                if(params.row.original?.destination_splits?.length  === 2)
                {
                    return <TwoLineCell headerText={params.row.original?.destination_splits[0]} descriptionText={params.row.original?.destination_splits[1]} />
                }

                return (
                    <div className={classes.caller}>{params.row.original.destination ?? ''}</div>
                );
            },
        },
        {
            accessor: 'voicemail_duration',
            Header: t<string>('screens:voicemails.duration'),
            width: 0.6,
            Cell: function Cell(params) {
                const duration = dayjs.duration(params.row.original.voicemail_duration ?? 0, 'seconds');
                const formatted = duration.format('HH:mm:ss');
                return (
                    <div className={classes.durationTextContainer}>{formatted}</div>
                );
            },
        },
        {
            accessor: 'flags',
            Header: t<string>('screens:voicemails.status'),
            width: 0.8,
            Cell: function Cell(params) {
                const hasAnswered = params.row.original.flags?.includes('\\Answered');
                const hasSeen = params.row.original.flags?.includes('\\Seen');
                const isNew = !hasSeen;
                return (<div className={classes.statusContainer}>
                    {isNew && (<div className={classNames(classes.badge, classes.activeBadge)}>
                        {t('screens:voicemails.new')}
                    </div>)}
                    {hasSeen && (<div className={classNames(classes.badge, classes.inactiveBadge)}>
                        {t('screens:voicemails.seen')}
                    </div>)}
                    {hasAnswered && (<div
                        className={classNames(classes.badge, classes.answered, hasSeen && classes.answeredOverSeen)}>
                        {t('screens:voicemails.answered')}
                    </div>)}
                </div>);
            },
        },
        {
            accessor: 'size',
            Header: t<string>('screens:voicemails.actions'),
            width: 1,
            Cell: function Cell(params) {
                return (
                    <div className={classes.centeredRecordingValue}>

                        <PlayFileButton
                            files={[{
                                call_recording_id: 'voicemail',
                                message_uid: params.row.original.message_uid,
                                playbackKey: voicemailPlaybackKey(params.row.original.message_uid),
                                h323_conf_id: 'voicemail',
                                customDownloadFileAction: (callback, restrictedCallback) => {
                                    downloadByMessageUid(params.row.original.message_uid, callback, restrictedCallback);
                                }
                            }]}
                            customClasses={{
                                beforePlaying: classes.playPauseButtonLoading
                            }}
                            dataQa={"play-file-icon"}
                            dataTestId={"play-file-icon-id"}
                            customPlayText={t('tooltips:voicemails.playVoicemail')}
                            customStopText={t('tooltips:voicemails.pauseVoicemail')}
                            skipPermission
                        />

                        <CustomizedIconButton
                            tooltipText={t(
                                'tooltips:voicemails.viewVoicemailRecord',
                            )}
                            dataQa="details-icon-button"
                            dataTestId="details-icon-button"
                            onClick={() => {
                                onClickDetails?.(params.row.original);
                            }}
                            skipPermission
                        >
                            <Assignment/>
                        </CustomizedIconButton>

                        <CustomizedIconButton
                            tooltipText={t(
                                'tooltips:voicemails.downloadVoicemail',
                            )}
                            dataQa="detail-download-icon"
                            dataTestId="download-icon"
                            onClick={() => {
                                downloadByMessageUid(params.row.original.message_uid, (url) => {
                                    saveObjectUrl(url, "voicemail_" + params.row.original.message_uid + ".wav");
                                }, undefined);
                            }}
                            skipPermission
                        >
                            <CloudDownload/>
                        </CustomizedIconButton>

                        <CustomizedIconButton
                            tooltipText={t(
                                'tooltips:voicemails.deleteVoicemail',
                            )}
                            dataQa="delete-icon"
                            dataTestId="delete-icon"
                            onClick={() => {
                                onClickDelete(params.row.original.message_uid);
                            }}
                        >
                            <Delete/>
                        </CustomizedIconButton>
                    </div>
                );
            },
        },
    ];
};

export const generateColumns = (
    t: TFunction<string>,
    classes: ReturnType<typeof useStyles>,
    timezoneOffset: number,
    userDateTimeFormat: string,
    downloadByMessageUid: (messageid: number, callback: (url: string) => void, restrictedCallback?: () => void) => void,
    onClickDelete: (messageid: number) => void,
    onClickStarred: (messageid: number, newValue: boolean) => void,
    onClickDetails: (voicemail: Voicemail) => void,
    faxMode: boolean,
    hasAnyVoicemailsBothWithFaxes: boolean
): Column<Voicemail>[] => {

    let returnArray: Column<Voicemail>[] = [
        {
            accessor: 'transcription_available',
            Header: '',
            width: 0.1,
            Cell: function Cell() {
                return (faxMode ? <FaxIcon /> : <UnifiedMessagingIcon />);
            },
        },
        {
            accessor: 'delivery_date',
            Header: t<string>('screens:voicemails.deliveryTime'),
            width: 0.8,
            Cell: function Cell(params) {
                return (
                    <DateTimeCell
                        timezoneOffset={timezoneOffset}
                        time={params.row.original.delivery_date}
                        customDateFormat={userDateTimeFormat}
                        showEmptyOnNoDate
                    />
                );
            },
        },
        {
            accessor: 'message_uid',
            Header: t<string>('screens:voicemails.sizeKb'),
            width: 0.6,
            Cell: function Cell(params) {
                return (
                    <div className={classes.sizeTextContainer}>{params.row.original.size ?? 0}</div>
                );
            },
        },
        {
            accessor: 'voicemail_duration',
            Header: t<string>('screens:voicemails.duration'),
            width: hasAnyVoicemailsBothWithFaxes ? 0.3 : 0.6,
            Cell: function Cell(params) {
                const durationValue = params.row.original.voicemail_duration ?? 0;
                const formatted = durationValue > 0
                    ? dayjs.duration(durationValue, 'seconds').format('HH:mm:ss')
                    : ' — ';
                return (
                    <div className={classes.durationTextContainer}>{formatted}</div>
                );
            },
        },
        {
            accessor: 'fax_pages',
            Header: t<string>('screens:voicemails.pages'),
            width: hasAnyVoicemailsBothWithFaxes ? 0.3 : 0.6,
            Cell: function Cell(params) {
                const pagesValue = params.row.original.fax_pages ?? 0;
                const formatted = pagesValue > 0
                    ? pagesValue + ''
                    : ' — ';
                return (
                    <div className={classes.durationTextContainer}>{formatted}</div>
                );
            },
        },
        {
            accessor: 'flags',
            Header: t<string>('screens:voicemails.status'),
            width: 0.8,
            Cell: function Cell(params) {
                const hasAnswered = params.row.original.flags?.includes('\\Answered');
                const hasSeen = params.row.original.flags?.includes('\\Seen');
                const isNew = !hasSeen;
                return (<div className={classes.statusContainer}>
                    {isNew && (<div className={classNames(classes.badge, classes.activeBadge)}>
                        {t('screens:voicemails.new')}
                    </div>)}
                    {hasSeen && (<div className={classNames(classes.badge, classes.inactiveBadge)}>
                        {t('screens:voicemails.seen')}
                    </div>)}
                    {hasAnswered && (<div
                        className={classNames(classes.badge, classes.answered, hasSeen && classes.answeredOverSeen)}>
                        {t('screens:voicemails.answered')}
                    </div>)}
                </div>);
            },
        },
        {
            accessor: 'not_used',
            Header: t<string>('screens:voicemails.flagged'),
            width: 0.4,
            Cell: function Cell(params) {
                const flagged = params.row.original.flags?.includes('\\Flagged');
                return (<div className={classes.statusContainer}>
                    <CustomizedIconButton
                        onClick={() => {
                            onClickStarred(params.row.original.message_uid, !flagged);
                        }}
                        dataTestId={'flagged-icon'}
                    >
                        <StarRate
                            className={flagged ? classes.starred : classes.notStarred}
                        />
                    </CustomizedIconButton>
                </div>);
            },
        },
        {
            accessor: 'size',
            Header: t<string>('screens:voicemails.actions'),
            width: 1,
            Cell: function Cell(params) {
                const isVoicemail = ((params.row.original.voicemail_duration ?? 0) > 0) || !faxMode;
                return (
                    <div className={classes.centeredRecordingValue}>
                        {isVoicemail ? (
                            <PlayFileButton
                                files={[{
                                    call_recording_id: 'voicemail',
                                    message_uid: params.row.original.message_uid,
                                    playbackKey: voicemailPlaybackKey(params.row.original.message_uid),
                                    h323_conf_id: 'voicemail',
                                    customDownloadFileAction: (callback, restrictedCallback) => {
                                        downloadByMessageUid(params.row.original.message_uid, callback, restrictedCallback);
                                    }
                                }]}
                                customClasses={{
                                    beforePlaying: classes.playPauseButtonLoading
                                }}
                                dataQa={"play-file-icon"}
                                dataTestId={"play-file-icon-id"}
                                customPlayText={t('tooltips:voicemails.playVoicemail')}
                                customStopText={t('tooltips:voicemails.pauseVoicemail')}
                                skipPermission
                            />
                        ) : (<></>)}

                        <CustomizedIconButton
                            tooltipText={t(isVoicemail
                                ? 'tooltips:voicemails.viewVoicemailRecord'
                                : 'tooltips:voicemails.viewFaxRecord',
                            )}
                            dataQa="details-icon-button"
                            dataTestId="details-icon-button"
                            onClick={() => {
                                onClickDetails?.(params.row.original);
                            }}
                            skipPermission
                        >
                            <Assignment/>
                        </CustomizedIconButton>

                        <CustomizedIconButton
                            tooltipText={t(isVoicemail
                                ? 'tooltips:voicemails.downloadVoicemail'
                                : 'tooltips:voicemails.downloadFax',
                            )}
                            dataQa="detail-download-icon"
                            dataTestId="download-icon"
                            onClick={() => {
                                downloadByMessageUid(params.row.original.message_uid, (url) => {
                                    saveObjectUrl(url, "voicemail_" + params.row.original.message_uid + ".wav");
                                }, undefined);
                            }}
                            skipPermission
                        >
                            <CloudDownload/>
                        </CustomizedIconButton>

                        <CustomizedIconButton
                            tooltipText={t(isVoicemail
                                ? 'tooltips:voicemails.deleteVoicemail'
                                : 'tooltips:voicemails.deleteFax',
                            )}
                            dataQa="delete-icon"
                            dataTestId="delete-icon"
                            onClick={() => {
                                onClickDelete(params.row.original.message_uid);
                            }}
                        >
                            <Delete/>
                        </CustomizedIconButton>
                    </div>
                );
            },
        },
    ];

    if(faxMode && !hasAnyVoicemailsBothWithFaxes) {
        returnArray = returnArray.filter(e => e.accessor != 'voicemail_duration');
    }

    if(!faxMode) {
        returnArray = returnArray.filter(e => e.accessor != 'fax_pages');
    }

    return returnArray;
};

export function saveObjectUrl(objectUrl: string, filename: string) {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
}

export const initialVoicemailDetailsDialogProps = {
    isOpen: false,
    voicemail: {} as Voicemail,
    extensionId: '',
    extensionName: '',
    i_account: 0,
    isFax: false,
} as VoicemailDetailsDialogProps;